import React, { useEffect, useRef, useState } from 'react';
import { CPGABI } from '../ABI/ABI';
import Loader from '../components/loader/Loader';
import ThemedSelect from '../components/ui/ThemedSelect';
import { CPGAddress } from '../constant/constant';
import { closeIcon } from '../constant/icon';
import {
    DISPUTE_RAISE_SUCCESSFULLY,
    TRANSACTION_ERROR_MSG,
    USER_REJECT_TRANSACTION_MSG,
} from '../constant/toasterMessage';
import { fireErrorToast, fireSuccessToast } from '../utils/commonFunction';
import { ContractWrite, readContractData } from '../utils/contractInstance';
import { resolveDisputeIdForPayment } from '../utils/cpgDiamondReader';
import { extractDisputeIdFromReceipt } from '../utils/disputeEvents';
import { waitForSuccessfulTransactionReceipt } from '../utils/transactionReceipt';
import axios from 'axios';
import { useAccount, useSignMessage } from 'wagmi';
import {
    API_BASE_URL,
    ensureBackendSession,
    getStoredBackendToken,
} from '../api/backendClient';
import {
    MAX_UPLOAD_BYTES,
    MAX_UPLOAD_LABEL,
    formatFileSizeError,
} from '../utils/attachmentUrl';

const DisputeModal = ({
    setDisputeModal,
    currentItem,
    setCurrentItem,
    getData,
    getSendLockedPaymentData,
    getReceiveLockedPaymentData,
}) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const [selectedName, setSelectedName] = useState('');
    const [description, setDescription] = useState('');
    const [reason, setReason] = useState('');
    const [proof, setProof] = useState('');
    const [loading, setLoading] = useState(false);
    const hasSubmittedRef = useRef(false);
    const { address: walletAddress } = useAccount();
    const { signMessageAsync } = useSignMessage();

    //******************************************************************** Smart Contract Call ************************************************************************//
    const {
        writeContract: raiseDispute,
        writeErrorResponse: raiseDisputeErrorResponse,
        transactionHash,
    } = ContractWrite({
        address: CPGAddress,
        abi: CPGABI,
        functionName: 'raiseDispute',
        initialArgs: [currentItem?.id?.toString()],
        enabled: true,
    });

    const handleChange = (e) => {
        setDescription(e.target.value);
    };

    const handleReasonChange = (e) => {
        setReason(e.target.value);
    };

    const handleProofChange = (e) => {
        setProof(e.target.value);
    };

    const onWriteError = (error) => {
        if (error) {
            if (
                !error?.details?.includes(
                    'MetaMask Tx Signature: User denied transaction signature',
                )
            ) {
                fireErrorToast(TRANSACTION_ERROR_MSG);
            }
            if (
                error?.details?.includes(
                    'MetaMask Tx Signature: User denied transaction signature',
                )
            ) {
                fireErrorToast(USER_REJECT_TRANSACTION_MSG);
            }
        }

        setLoading(false);
        // }
    };

    useEffect(() => {
        if (transactionHash && !hasSubmittedRef.current) {
            hasSubmittedRef.current = true;
            submitDisputeDetails(transactionHash);
        }
    }, [transactionHash]);

    const finalizeSuccessfulRaise = () => {
        setDisputeModal(false);
        setLoading(false);
        setCurrentItem({});
        fireSuccessToast(DISPUTE_RAISE_SUCCESSFULLY);
        getData();
        getSendLockedPaymentData();
        getReceiveLockedPaymentData();
    };

    const resolveOnChainDisputeId = async (txHashValue) => {
        const receipt = await waitForSuccessfulTransactionReceipt(txHashValue);
        let latestDisputeId = extractDisputeIdFromReceipt(receipt);
        if (latestDisputeId === null || latestDisputeId === undefined) {
            for (let attempt = 0; attempt < 4; attempt += 1) {
                latestDisputeId = await resolveDisputeIdForPayment(
                    readContractData,
                    CPGAddress,
                    CPGABI,
                    currentItem?.id,
                );
                if (latestDisputeId !== null && latestDisputeId !== undefined) {
                    break;
                }
                await new Promise((resolve) => {
                    setTimeout(resolve, 800 * (attempt + 1));
                });
            }
        }
        return latestDisputeId;
    };

    const submitDisputeDetails = async (txHashValue) => {
        console.log(
            'Submitting dispute details for paymentId:',
            currentItem?.id,
        );
        try {
            const latestDisputeId = await resolveOnChainDisputeId(txHashValue);

            const formData = new FormData();
            formData.append('paymentId', currentItem?.id?.toString());
            if (latestDisputeId !== null && latestDisputeId !== undefined) {
                formData.append('disputeId', latestDisputeId.toString());
            }
            if (txHashValue) {
                formData.append('txHash', txHashValue.toLowerCase());
            }
            formData.append('description', description);
            formData.append('reason', reason);
            formData.append('proof', proof);
            if (selectedFile) {
                formData.append('attachment', selectedFile);
            }
            if (walletAddress) {
                formData.append('raisedByWallet', walletAddress);
            }
            const taskIdRaw =
                currentItem?.taskId ?? currentItem?.task_id ?? null;
            if (taskIdRaw != null && taskIdRaw !== '') {
                formData.append('taskId', String(taskIdRaw));
            }
            // Participant wallets are derived server-side from the Diamond.
            // Do not send caller-controlled membership fields.

            let token = getStoredBackendToken(walletAddress);
            if (!token && walletAddress && signMessageAsync) {
                token = await ensureBackendSession(walletAddress, {
                    signMessageAsync,
                    rolePreference: 'employee',
                });
            }
            if (!token) {
                throw new Error(
                    'Please sign in with your wallet before attaching dispute evidence.',
                );
            }

            const response = await axios.post(
                `${API_BASE_URL}/disputes/submit-payment-dispute`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                },
            );

            console.log(
                'Dispute details submitted successfully:',
                response.data,
            );

            finalizeSuccessfulRaise();
        } catch (error) {
            console.error('Error submitting dispute details:', error);
            console.error('Error response:', error.response?.data);
            const detail =
                error.response?.data?.message ||
                'Dispute raised but failed to save details. Please contact support.';
            fireErrorToast(detail);
            setLoading(false);
        }
    };

    useEffect(() => {
        if (raiseDisputeErrorResponse) {
            onWriteError(raiseDisputeErrorResponse);
        }
        // eslint-disable-next-line
    }, [raiseDisputeErrorResponse]);
    //******************************************************************** Smart Contract Call ************************************************************************//

    const handleAcceptButton = () => {
        try {
            setLoading(true);
            raiseDispute({
                abi: CPGABI,
                address: CPGAddress,
                functionName: 'raiseDispute',
                args: [currentItem?.id?.toString()],
            });
        } catch (error) {
        } finally {
        }
    };

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        event.target.value = '';
        if (!file) return;
        if (file.size > MAX_UPLOAD_BYTES) {
            fireErrorToast(formatFileSizeError());
            setSelectedFile(null);
            setSelectedName('');
            return;
        }
        setSelectedFile(file);
        setSelectedName(file.name);
    };

    return (
        <div
            tabIndex="-1"
            className="flex justify-center items-center overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none backdrop-brightness-50"
        >
            <div className="mx-5">
                <div className="relative my-6 lg:min-w-[550px] md:min-w-[50vw] sm:min-w-[70vw] min-w-[90vw] w-full">
                    <div className="glass-card rounded-2xl shadow-2xl relative flex flex-col outline-none focus:outline-none w-full">
                        {/* Header */}
                        <div className="flex items-center justify-between rounded-t-2xl border-b border-zinc-200 p-5 dark:border-zinc-800">
                            <div className="flex items-center gap-3">
                                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-rose-500/10 dark:bg-rose-950/50">
                                    <i className="fa-solid fa-gavel text-rose-600 dark:text-rose-400" />
                                </div>
                                <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
                                    Raise Dispute
                                </h3>
                            </div>
                            <button
                                type="button"
                                className="flex h-8 w-8 items-center justify-center rounded-lg bg-zinc-100 transition-colors hover:bg-zinc-200 dark:bg-zinc-800 dark:hover:bg-zinc-700"
                                onClick={() => setDisputeModal(false)}
                            >
                                <i
                                    className={`${closeIcon} text-sm text-zinc-500 dark:text-zinc-400`}
                                />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="p-5">
                            <div className="max-h-[60vh] space-y-5 overflow-y-auto overflow-x-hidden sm:max-h-[60vh] md:max-h-[58vh]">
                                {/* Payment ID */}
                                <div>
                                    <label className="mb-2 block text-sm font-medium text-zinc-700 dark:text-zinc-300">
                                        Payment ID
                                    </label>
                                    <input
                                        type="text"
                                        value={currentItem.id}
                                        disabled
                                        className="cpg-input cursor-not-allowed opacity-90 dark:bg-zinc-900 dark:text-zinc-400"
                                    />
                                </div>

                                {/* Description */}
                                <div>
                                    <label className="mb-2 block text-sm font-medium text-zinc-700 dark:text-zinc-300">
                                        Description{' '}
                                        <span className="text-rose-600 dark:text-rose-400">*</span>
                                    </label>
                                    <textarea
                                        value={description}
                                        rows={3}
                                        placeholder="Describe the issue with this payment..."
                                        className="cpg-input min-h-[88px] resize-y"
                                        onChange={handleChange}
                                    />
                                </div>

                                {/* Reason */}
                                <div>
                                    <label className="mb-2 block text-sm font-medium text-zinc-700 dark:text-zinc-300">
                                        Reason for Dispute{' '}
                                        <span className="text-rose-600 dark:text-rose-400">*</span>
                                    </label>
                                    <ThemedSelect
                                        value={reason}
                                        onChange={handleReasonChange}
                                        placeholder="Select a reason"
                                        options={[
                                            {
                                                value: '',
                                                label: 'Select a reason',
                                                disabled: true,
                                            },
                                            {
                                                value: 'non_delivery',
                                                label: 'Service/Product not delivered',
                                            },
                                            {
                                                value: 'poor_quality',
                                                label: 'Poor quality work',
                                            },
                                            {
                                                value: 'late_delivery',
                                                label: 'Late delivery',
                                            },
                                            {
                                                value: 'wrong_amount',
                                                label: 'Incorrect amount charged',
                                            },
                                            {
                                                value: 'unauthorized',
                                                label: 'Unauthorized transaction',
                                            },
                                            { value: 'other', label: 'Other' },
                                        ]}
                                    />
                                </div>

                                {/* Proof/Details */}
                                <div>
                                    <label className="mb-2 block text-sm font-medium text-zinc-700 dark:text-zinc-300">
                                        Additional Proof/Details
                                    </label>
                                    <textarea
                                        value={proof}
                                        rows={3}
                                        placeholder="Provide any additional evidence, links, or details that support your dispute..."
                                        className="cpg-input min-h-[88px] resize-y"
                                        onChange={handleProofChange}
                                    />
                                </div>

                                {/* File Upload */}
                                <div>
                                    <label className="mb-2 block text-sm font-medium text-zinc-700 dark:text-zinc-300">
                                        Attachment (optional)
                                    </label>
                                    <p className="mb-2 text-[11px] leading-relaxed text-zinc-500 dark:text-zinc-500">
                                        PNG, JPG, PDF, DOC, or DOCX. Max{' '}
                                        {MAX_UPLOAD_LABEL}. If the form fails
                                        after signing, check type and size—oversized
                                        files are rejected by the server.
                                    </p>
                                    <div className="group relative cursor-pointer rounded-xl border-2 border-dashed border-zinc-300 p-5 text-center transition-colors hover:border-teal-500/50 dark:border-zinc-700 dark:hover:border-teal-600/40">
                                        <input
                                            type="file"
                                            accept=".png,.jpg,.jpeg,.pdf,.doc,.docx"
                                            onChange={handleFileChange}
                                            className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                                        />
                                        <div className="flex flex-col items-center gap-2">
                                            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-zinc-100 transition-colors group-hover:bg-teal-50 dark:bg-zinc-800 dark:group-hover:bg-teal-950/40">
                                                <i className="fa-solid fa-cloud-arrow-up text-zinc-500 transition-colors group-hover:text-teal-700 dark:text-zinc-400 dark:group-hover:text-teal-400" />
                                            </div>
                                            <p className="text-sm text-zinc-500 dark:text-zinc-400">
                                                Click to upload a file
                                            </p>
                                        </div>
                                    </div>
                                    {selectedFile && (
                                        <div className="mt-2 flex items-center gap-2 rounded-lg border border-teal-200 bg-teal-50 px-3 py-2 dark:border-teal-900/50 dark:bg-teal-950/40">
                                            <i className="fa-solid fa-paperclip text-xs text-teal-700 dark:text-teal-400" />
                                            <span className="flex-1 truncate text-sm text-teal-900 dark:text-teal-200">
                                                {selectedName}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setSelectedFile(null);
                                                    setSelectedName('');
                                                }}
                                                className="text-zinc-400 transition-colors hover:text-rose-500 dark:text-zinc-500 dark:hover:text-rose-400"
                                            >
                                                <i className="fa-solid fa-xmark text-xs" />
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Submit Button */}
                                <div>
                                    {loading ? (
                                        <div className="flex w-full items-center justify-center rounded-xl bg-zinc-100 py-3 dark:bg-zinc-900">
                                            <Loader
                                                size={'20px'}
                                                stroke={'#0d9488'}
                                            />
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            className="w-full rounded-xl bg-teal-700 px-5 py-3 text-center text-base font-medium text-white shadow-md shadow-teal-900/20 transition-all hover:bg-teal-600 disabled:cursor-not-allowed disabled:opacity-40 dark:bg-teal-600 dark:hover:bg-teal-500"
                                            disabled={!description || !reason}
                                            onClick={handleAcceptButton}
                                        >
                                            <i className="fa-solid fa-gavel mr-2" />
                                            Submit Dispute
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DisputeModal;
